import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow } from './InventoryShared';

export default function InventoryCategories() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/categories`;
      if (search) url += `?search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      setList(await res.json());
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const removeItem = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    const res = await fetch(`${API}/companies/${companyId}/inventory/categories/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📂 Product Categories</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Category</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Name', 'Description', 'Products', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No categories</td></tr>}
              {list.map(c => (
                <tr key={c.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                  <td style={tdStyle}>{c.description || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.products_count ?? 0}</td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(c)}>Edit</button>
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(c.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <CategoryModal companyId={companyId} category={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
    </div>
  );
}

function CategoryModal({ companyId, category, onClose, onSaved }) {
  const isNew = !category.id;
  const [form, setForm] = useState({ name: category.name || '', description: category.description || '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/inventory/categories` : `${API}/companies/${companyId}/inventory/categories/${category.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Category' : 'Edit Category'}</h3>
        <FormRow label="Name *"><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormRow>
        <FormRow label="Description"><textarea style={{ ...inputStyle, minHeight: 70 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
